// drafts.js — browser-local "save draft" for long forms (Vendor OS).
//
// Saves the TEXT fields of a controlled form to localStorage so a half-filled
// form survives a navigation away, a refresh, or an accidental close. Files are
// NOT stored — localStorage holds strings only, so uploaded documents/PDFs are
// deliberately left out; the user re-attaches them when they resume. Every text
// field restores exactly.
//
// Drafts are scoped per key (register drafts keyed by staff email, invoice
// drafts by vendor id) so two people sharing a machine never clobber each
// other's work-in-progress.
//
// Lives in src/ (not src/prototype/, which the port script regenerates) so it
// survives a future v4 design re-port.

import { useCallback, useEffect, useRef, useState } from 'react';

const PREFIX  = 'vendoros:draft:';
const VERSION = 1;   // bump to invalidate older draft shapes after a form change

const storageKey = (key) => PREFIX + key;

/** Read a stored draft. Returns { form, savedAt } or null. */
export function readDraft(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || obj.v !== VERSION || !obj.form) return null;
    return { form: obj.form, savedAt: obj.savedAt || null };
  } catch (_) { return null; }
}

/** Persist a draft. Returns true on success. */
export function writeDraft(key, form, savedAt) {
  if (!key) return false;
  try {
    localStorage.setItem(storageKey(key), JSON.stringify({
      v: VERSION, form, savedAt: savedAt || nowIso(),
    }));
    return true;
  } catch (_) { return false; }   // quota / private-mode → fail soft
}

/** Delete a stored draft. */
export function clearDraft(key) {
  if (!key) return;
  try { localStorage.removeItem(storageKey(key)); } catch (_) {}
}

// new Date().toISOString() is fine in the browser (the Date-now ban is a
// workflow-script constraint, not a frontend one).
function nowIso() { return new Date().toISOString(); }

// Value-equality for flat form objects (string/number primitives only).
function sameForm(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a), kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (String(a[k] ?? '') !== String(b[k] ?? '')) return false;
  }
  return true;
}

/**
 * Human "saved 2m ago" label. Pass the screen's `t` so it localizes; without
 * it the English template strings are used verbatim.
 */
export function savedAgo(iso, t) {
  if (!iso) return '';
  const fmt = (s, vars) => {
    if (t) return t(s, vars);
    let out = s;
    if (vars) for (const k in vars) out = out.split('{' + k + '}').join(String(vars[k]));
    return out;
  };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 45) return fmt('just now');
  const min = Math.round(sec / 60);
  if (min < 60) return fmt('{n}m ago', { n: min });
  const hr = Math.round(min / 60);
  if (hr < 24) return fmt('{n}h ago', { n: hr });
  return d.toLocaleDateString();
}

/**
 * Form-draft hook.
 *
 * @param {object}   o
 * @param {string}   o.key       Stable per-user / per-vendor draft key. Falsy
 *                               disables the hook entirely (no read/write).
 * @param {object}   o.form      Current controlled-form state.
 * @param {Function} o.setForm   React setter for the form.
 * @param {object}   o.defaults  The empty/initial form — used to detect a still
 *                               -blank form so we never persist an untouched one.
 * @param {boolean} [o.enabled]  Extra gate for auto-save (default true).
 *
 * Returns:
 *   pendingDraft  { form, savedAt } found on mount before the user acts, or null
 *   restore()     load pendingDraft into the form and dismiss the banner
 *   discard()     delete the stored draft and dismiss the banner
 *   saveNow()     force an immediate save (the explicit "Save draft" button)
 *   clear()       delete the stored draft (call after a successful submit)
 *   savedAt       ISO timestamp of the last save (for the "saved · 2m ago" line)
 */
export function useFormDraft({ key, form, setForm, defaults, enabled = true }) {
  const [pendingDraft, setPendingDraft] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const lastSavedRef = useRef(null);    // last form we persisted (change-detect)
  const loadedKeyRef = useRef(null);    // guards one-time load per key
  const defaultsRef  = useRef(defaults);
  defaultsRef.current = defaults;

  const active = !!(enabled && key);

  // One-time load of an existing draft when the key becomes available.
  useEffect(() => {
    if (!active || loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    const d = readDraft(key);
    if (d && !sameForm(d.form, defaultsRef.current)) {
      setPendingDraft(d);
      lastSavedRef.current = d.form;     // don't immediately re-save what we read
      setSavedAt(d.savedAt);
    }
  }, [active, key]);

  const isBlank = sameForm(form, defaults);

  // Debounced auto-save. Skips while the form is still blank (so an untouched
  // form never overwrites a real draft) and while it matches what we last saved.
  // We do NOT pause on a pending banner: once the user types, their new content
  // is saved; the original draft stays recoverable from `pendingDraft` (held in
  // memory) until they Restore or Discard it.
  useEffect(() => {
    if (!active || isBlank) return;
    if (sameForm(form, lastSavedRef.current)) return;
    const id = setTimeout(() => {
      const stamp = nowIso();
      if (writeDraft(key, form, stamp)) {
        lastSavedRef.current = form;
        setSavedAt(stamp);
      }
    }, 700);
    return () => clearTimeout(id);
  }, [active, key, form, isBlank]);

  const restore = useCallback(() => {
    if (pendingDraft) {
      setForm(pendingDraft.form);
      lastSavedRef.current = pendingDraft.form;   // it's already persisted
    }
    setPendingDraft(null);
  }, [pendingDraft, setForm]);

  const discard = useCallback(() => {
    clearDraft(key);
    lastSavedRef.current = null;
    setSavedAt(null);
    setPendingDraft(null);
  }, [key]);

  const saveNow = useCallback(() => {
    if (!key) return false;
    const stamp = nowIso();
    if (writeDraft(key, form, stamp)) {
      lastSavedRef.current = form;
      setSavedAt(stamp);
      setPendingDraft(null);   // an explicit save supersedes the restore prompt
      return true;
    }
    return false;
  }, [key, form]);

  const clear = useCallback(() => {
    clearDraft(key);
    lastSavedRef.current = null;
    setSavedAt(null);
    setPendingDraft(null);
  }, [key]);

  return { pendingDraft, restore, discard, saveNow, clear, savedAt };
}
